import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { delay } from '@libs/utils/helpers/delay';
import { getApp } from '@libs/utils/helpers/getApplication';
import { getCookie } from '@libs/utils/helpers/getSafeCookies';
import { isUserFromWebView, redirectInApp } from '@libs/utils/helpers/webviewComunication';

import { SHOffer } from '../services/sortingHat/types';
import { sendFireboltSoftProposal, startFullFunnelProposal } from '../services/firebolt';

import { ExtraFunctionsType } from '../types';

import { redirect } from '../helpers/redirect';
import { getUrl } from '../helpers/getRedirectUrl';
import { getSafeUser } from '../helpers/getSafeUser';

import { useCreditTrack } from './useCreditTrack';

type UseOfferRedirectProps = {
  location?: string;
  user: any;
  extraFunctions?: ExtraFunctionsType;
};

type HandleOfferRedirectParams = {
  offer: SHOffer;
  index?: number;
  withClickEvents?: boolean;
};

export function useOfferRedirect({ location = '', user, extraFunctions }: UseOfferRedirectProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { productClickedEvent, redirectRequestedEvent, proposalStartedEvent, elementClickedEvent } = useCreditTrack();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRedirectModalOpen, setIsRedirectModalOpen] = useState(false);

  const handleOfferRedirect = useCallback(
    async ({ offer, index = 0, withClickEvents = true }: HandleOfferRedirectParams) => {
      setIsLoading(true);
      setIsRedirectModalOpen(true);

      if (withClickEvents) {
        elementClickedEvent({
          elementType: 'button',
          location: pathname,
          name: `${location} - ${offer?.content?.button_text}`,
        });
      }

      extraFunctions?.beforeSendProposal?.();

      const safeUser = getSafeUser(user);
      const { generalInfo, content } = offer;
      const { slug, partner } = generalInfo;
      const { full_funnel } = content;
      const isAc = partner === 'acordocerto';
      const isCp = getApp() === 'consumidorpositivo';

      if (isAc && isCp) {
        setIsRedirectModalOpen(false);
        setIsLoading(false);
        if (withClickEvents) {
          productClickedEvent({ offer, index, location, document: safeUser?.cpf });
        }
        return navigate('/debitos');
      }

      const proposalResponse = full_funnel
        ? await startFullFunnelProposal(offer)
        : await sendFireboltSoftProposal({
            ...safeUser,
            chosenCard: slug,
            partner,
          });

      const fireboltId = proposalResponse?.firebolt_id;
      const fireboltSourceUrl = proposalResponse?.webhookResult?.redirectUrl;
      const storyblokSourceUrl = await getUrl({ offer, fireboltId, user: safeUser });

      extraFunctions?.afterSendProposal?.(fireboltId);

      if (withClickEvents) {
        productClickedEvent({ offer, index, location, document: safeUser?.cpf });
      }

      const url = fireboltSourceUrl || storyblokSourceUrl;

      extraFunctions?.beforeRedirect?.(fireboltId, url);

      if (fireboltId) {
        await proposalStartedEvent({
          offer,
          redirectUrl: url,
          fireboltId,
          document: safeUser?.cpf,
        });
      }

      if (isUserFromWebView() && getCookie('app_version')) {
        setIsRedirectModalOpen(false);
        setIsLoading(false);
        return redirectInApp(url);
      }

      redirect(url);
      setIsRedirectModalOpen(false);
      setIsLoading(false);
      redirectRequestedEvent({
        fireboltId,
        offerSlug: offer?.content?.slug,
        originPath: pathname,
        redirectURL: url,
        document: safeUser?.cpf,
      });

      extraFunctions?.afterRedirect?.(fireboltId, url);
    },
    [user]
  );

  async function handleDetailRedirect(offer: SHOffer, index = 0) {
    elementClickedEvent({
      elementType: 'card/button',
      name: offer.content.slug,
      location,
      text: 'Ver mais detalhes',
      position: `${index}`,
    });

    const detailUrl = `/oferta-detalhada/${offer.content.slug}`;
    await delay(150);
    return navigate(detailUrl);
  }
  //#endregion

  return {
    isLoading,
    handleOfferRedirect,
    handleDetailRedirect,
    isRedirectModalOpen,
  };
}
